/**
 * Silent web-app JWT refresh (Dashboard / admin portal).
 * Mirrors the extension Classroom ensure-session behavior:
 * call /api/auth/refresh before forcing the user to sign in again.
 */
import apiConfig from '../config/api';
import {
  silentRefreshAccessToken as silentRefreshAccessTokenCore,
  fetchWithAuthRetry as fetchWithAuthRetryCore,
  checkTokenExpiry
} from './silentAuthRefreshCore.js';

const IMPERSONATION_TOKEN_KEY = 'trafficLiteImpersonationToken';

export type SilentRefreshResult = {
  ok: boolean;
  token: string | null;
  refreshed: boolean;
  reason?: string;
};

let refreshInFlight: Promise<SilentRefreshResult> | null = null;

const getRefreshUrl = (): string => {
  const apiBase = String(apiConfig.API_BASE_URL || '').replace(/\/$/, '');
  return `${apiBase}/auth/refresh`;
};

const readBrowserToken = (): string | null => (
  sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)
  ?? localStorage.getItem('trafficLiteToken')
);

const persistRefreshedToken = (previousToken: string, nextToken: string) => {
  const impersonationToken = sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);
  if (impersonationToken && impersonationToken === previousToken) {
    sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, nextToken);
  }

  const mainToken = localStorage.getItem('trafficLiteToken');
  if (!impersonationToken || mainToken === previousToken || !mainToken) {
    localStorage.setItem('trafficLiteToken', nextToken);
  }
};

const browserDeps = () => ({
  fetchFn: fetch.bind(globalThis),
  refreshUrl: getRefreshUrl(),
  readToken: readBrowserToken,
  writeToken: persistRefreshedToken
});

/**
 * Refresh the access JWT when expired/expiring, or when force=true (e.g. after a 401).
 * Concurrent callers share one in-flight refresh.
 */
export async function silentRefreshAccessToken(options?: {
  force?: boolean;
  token?: string | null;
  bufferMinutes?: number;
}): Promise<SilentRefreshResult> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const result = await silentRefreshAccessTokenCore(options, browserDeps());
    return result as SilentRefreshResult;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/** Ensure a usable access token; returns null only when refresh cannot recover the session. */
export async function ensureFreshAccessToken(options?: {
  force?: boolean;
  token?: string | null;
}): Promise<string | null> {
  const result = await silentRefreshAccessToken(options);
  return result.ok ? result.token : null;
}

/**
 * fetch() with Bearer token. On 401, try one silent refresh + retry.
 * Does not clear local auth — callers decide when to force sign-in.
 */
export async function fetchWithAuthRetry(
  input: string,
  init: RequestInit = {},
  options?: { token?: string | null }
): Promise<Response> {
  return fetchWithAuthRetryCore(input, init, {
    ...browserDeps(),
    token: options?.token
  });
}

export { checkTokenExpiry };
